import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { selectTasks } from '~/reducers/tasksSlice';
import { selectIsOpenUnassignedTasksDrawer } from '~/reducers/mapDrawerSettingsSlice';
import {
    resetSelectedMapStops,
    selectSelectedMapStops
} from '~/reducers/selectedMapStopsSlice';
import { ManageStopControl } from '~/ui';
import { ResequenceModal } from '~/components/MapPage/ResequenceModal/ResequenceModal';
import SingleStopControl from './SingleStopControl';
import MultiStopsControl from './MultiStopsControl';
import { mapPlanStopUtils } from '~/utils/map';
import { usePlanMapPropsContext } from '~/components/MapPage/PlanMap/PlanMapPropsContext';
import ReassignModal from '~/components/MapPage/ReassignModal';
import constants from '~/utils/constants';
import { useStopResequence } from '~/hooks';

import './PlanStopsControl.scss';

const stopIdPropertyName = 'clientRouteTaskId';
const { views } = constants.stopsControl;

export const PlanStopsControl = () => {
    const dispatch = useDispatch();
    const { planStops, routesLevelData } = usePlanMapPropsContext();
    const { resequenceStops } = useStopResequence();
    const tasksData = useSelector(selectTasks);
    const isOpenUnassignedTasksDrawer = useSelector(
        selectIsOpenUnassignedTasksDrawer
    );
    const selectedMapStopsIds = useSelector(selectSelectedMapStops);
    const [activeView, setActiveView] = useState(views.MAIN);

    const { t } = useTranslation('translation');

    const {
        firstSelectedPlanStopData,
        isDifferentRoutesSelected,
        isMultiStopsSelected,
        allPlanStops = [],
        selectedPlanStops = [],
        unselectedPlanStops = []
    } = useMemo(() => {
        if (!selectedMapStopsIds.length) {
            if (activeView !== views.MAIN) setActiveView(views.MAIN);
            return {};
        }

        const stopsData = isOpenUnassignedTasksDrawer
            ? mapPlanStopUtils.mapIdtoTasksData(selectedMapStopsIds, tasksData)
            : planStops;

        const { allStops, firstSelectedStop, selectedStops, unselectedStops } =
            mapPlanStopUtils.mapIdtoStopData(stopsData, selectedMapStopsIds);
        return {
            allPlanStops: allStops,
            firstSelectedPlanStopData: firstSelectedStop,
            isMultiStopsSelected: selectedStops.length > 1,
            selectedPlanStops: selectedStops,
            unselectedPlanStops: unselectedStops,
            isDifferentRoutesSelected:
                isOpenUnassignedTasksDrawer ||
                mapPlanStopUtils.stopsBelongToDifferentParentRoutes(
                    selectedStops,
                    routesLevelData
                )
        };
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [planStops, selectedMapStopsIds, activeView]);

    const deselectButtonClickHandler = useCallback(() => {
        dispatch(resetSelectedMapStops());
    }, [dispatch]);

    const onCloseHandler = useCallback(() => setActiveView(views.MAIN), []);

    const onResequence = useCallback(
        (selectedId, isInsertAfter = true) => {
            const selectedStop = allPlanStops.find(
                (stop) => stop[stopIdPropertyName] === selectedId
            );

            if (!selectedStop) {
                console.warn('Stop not selected');
                return;
            }

            const {
                stopNumber: targetStopNumber,
                clientRouteId: targetClientRouteId,
                routeId: targetRouteId
            } = selectedStop;
            resequenceStops({
                targetRouteId,
                targetClientRouteId,
                targetStopNumber,
                isInsertAfter
            });
        },
        [allPlanStops, resequenceStops]
    );

    if (!firstSelectedPlanStopData) return null;

    return (
        <ManageStopControl
            className="plan-stops-control"
            data-testid="plan-stops-control"
        >
            {activeView === views.MAIN && (
                <>
                    {isMultiStopsSelected ? (
                        <MultiStopsControl
                            selectedPlanStops={selectedPlanStops}
                            stopIdPropertyName={stopIdPropertyName}
                            disableResequencing={isDifferentRoutesSelected}
                            footerButtonTitle={t(
                                'PlanStopsControl.button.deselectAll'
                            )}
                            footerButtonIcon="iconClose"
                            onClickFooterButton={deselectButtonClickHandler}
                            onSwitchView={setActiveView}
                        />
                    ) : (
                        <SingleStopControl
                            stopData={firstSelectedPlanStopData}
                            footerButtonTitle={t(
                                'PlanStopsControl.button.deselect'
                            )}
                            footerButtonIcon="iconClose"
                            onClickFooterButton={deselectButtonClickHandler}
                            onSwitchView={setActiveView}
                            disableResequencing={isDifferentRoutesSelected}
                        />
                    )}
                </>
            )}
            {activeView === views.RESEQUENCE && (
                <ResequenceModal
                    clickHandler={onCloseHandler}
                    isDifferentRoutesSelected={isDifferentRoutesSelected}
                    selectedStopsData={selectedPlanStops}
                    unselectedStopsData={unselectedPlanStops}
                    stopIdPropertyName={stopIdPropertyName}
                    onResequence={onResequence}
                />
            )}
            {activeView === views.REASSIGN && (
                <ReassignModal
                    onClose={onCloseHandler}
                    routes={routesLevelData}
                    selectedStops={selectedPlanStops}
                    stopIdPropertyName={stopIdPropertyName}
                />
            )}
        </ManageStopControl>
    );
};
